import { Card, Col, message, Modal, PageHeader, Row, Select, Space, Spin, Statistic, Table, Tabs, Tag, Tooltip } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import moment from "moment";
import "moment/locale/es";
import { FileExcelOutlined, InfoCircleOutlined, SettingOutlined } from "@ant-design/icons";
import {
  ButtonItem,
  ErrorItem,
  Form,
  FormItem,
  RangePickerItem,
  SelectItem,
  SwitchItem,
  cuentaMask,
  dineroDisabledMask,
} from "../../../../Components/Items";
import { nombreEmpresa } from "../../../../Components/ExcelEstilos";
import {
  ETIQUETA_ORIGEN,
  ETIQUETA_TIPO_COLUMNA,
  MODOS_COLUMNA,
  agruparRenglones,
  construirColumnas,
  movimientosDeCelda,
  textoCuentas,
  tieneDetalle,
} from "./columnasFlujo";
import { descargarExcel } from "./exportarFlujoDeEfectivoDiario";
import ModalConfiguracionFlujo from "./ModalConfiguracionFlujo";

moment.locale("es");

const { Option } = Select;
const { TabPane } = Tabs;

/*
  Flujo De Efectivo Diario: replica del flujo que Proaliv llevaba a mano en Excel.

  Una columna por dia. Antes de hoy son movimientos reales de los bancos; de hoy en
  adelante, la proyeccion con las cuentas por cobrar y por pagar pendientes. El
  backend manda los renglones YA en orden de presentacion (ESTRUCTURA en
  erp_backend/Utils/flujoEfectivoDiario.reglas.js) con un valor por dia; aqui solo se
  agrupan las columnas (dia / semana / mes) y se pintan. La tabla y el Excel salen de
  la misma agrupacion para que no puedan decir cosas distintas.

  La tabla usa `Table` de antd y no `TableItem`: TableItem fija por dentro `scroll` y
  `pagination` e ignora esos props, y aqui hace falta la columna de conceptos fija y
  scroll horizontal.
*/

const ROJO_NEGATIVO = "#CF1322";

const ESTILO_RENGLON = {
  titulo: { backgroundColor: "#007A8A", color: "white", fontWeight: 700 },
  seccion: { backgroundColor: "#F0FEFF", color: "#005F6B", fontWeight: 600 },
  total: { backgroundColor: "#EAF6F8", fontWeight: 600 },
  totalFuerte: { backgroundColor: "#C9EEF3", fontWeight: 700 },
  saldo: { backgroundColor: "#C9EEF3", fontWeight: 700 },
  saldoSuave: { fontWeight: 600 },
  informativo: { color: "#8C8C8C", fontStyle: "italic" },
};

const TOTALES_FUERTES = new Set(["tot_ingresos", "tot_egresos", "iva_resumen"]);
const SALDOS_FUERTES = new Set(["rem_remanente", "rem_saldoInicial", "rem_disponible"]);

const estiloDeRenglon = (renglon) => {
  if (renglon.tipo === "total") return TOTALES_FUERTES.has(renglon.clave) ? ESTILO_RENGLON.totalFuerte : ESTILO_RENGLON.total;
  if (renglon.tipo === "saldo") return SALDOS_FUERTES.has(renglon.clave) ? ESTILO_RENGLON.saldo : ESTILO_RENGLON.saldoSuave;
  return ESTILO_RENGLON[renglon.tipo] || {};
};

const COLOR_ENCABEZADO = {
  real: { backgroundColor: "#00BFD6", color: "white" },
  hoy: { backgroundColor: "#007A8A", color: "white" },
  proyectado: { backgroundColor: "#D6F4F8", color: "#005F6B" },
};

const COLOR_TAG_ORIGEN = { real: "cyan", proyectado: "blue", presupuesto: "purple", programado: "gold" };

const formatoPeriodo = (periodo) =>
  periodo && periodo[0] && periodo[1]
    ? [moment(periodo[0]).format("YYYY-MM-DD"), moment(periodo[1]).format("YYYY-MM-DD")]
    : [];

class ReporteFlujoDeEfectivoDiario extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    dataReporte: null,
    modoColumnas: "dia",
    objetoProyectos: null,
    objetoUnidadesDeNegocio: null,
    objetoBancos: null,
    modalDetalle: false,
    detalleTitulo: "",
    detalleMovimientos: [],
    modalConfiguracion: false,
    // Los filtros de la ultima consulta: al guardar la configuracion se vuelve a correr
    // el reporte con los mismos.
    ultimosValores: null,
  };

  atras() {
    this.props.history.goBack();
  }

  async componentDidMount() {
    this.setState({ loading: true });
    const headers = { headers: this.props.headersToken };
    const cuerpo = { empresaId: this.props.empresaId };
    await Promise.all([
      axios.post("proyectos/selectProyectos", cuerpo, headers).then((res) => {
        if (res.data.status !== 404 && Array.isArray(res.data)) {
          this.setState({
            objetoProyectos: res.data.map((value) => (
              <Option value={value._id} key={value._id}>
                {value.nombre}
              </Option>
            )),
          });
        }
      }),
      axios.post("unidadesdenegocio/selectUnidadesDeNegocio", cuerpo, headers).then((res) => {
        if (res.data.status !== 404 && Array.isArray(res.data)) {
          this.setState({
            objetoUnidadesDeNegocio: res.data.map((value) => (
              <Option value={value._id} key={value._id}>
                {`${cuentaMask(
                  (value.nivel1 || "") + (value.nivel2 || "") + (value.nivel3 || "") + (value.nivel4 || "") + (value.nivel5 || "")
                )} - ${value.nombre}`}
              </Option>
            )),
          });
        }
      }),
      axios.post("reportesFinancieros/bancosFlujoDeEfectivoDiario", cuerpo, headers).then((res) => {
        if (res.data.success === 2) {
          this.setState({
            objetoBancos: res.data.bancos.map((banco) => (
              <Option value={banco._id} key={banco._id}>
                {`${[banco.banco, banco.nombre].filter(Boolean).join(" - ")}${banco.caja ? " (Caja)" : ""}${
                  banco.estado === 1 ? "" : " (Inactivo)"
                }`}
              </Option>
            )),
          });
        }
      }),
    ]).catch(() => {});

    this.consultarReporte({
      periodo: [moment().startOf("month"), moment().add(30, "days")],
      bancos: [],
      incluirCajas: false,
      modoFecha: "pago",
      incluirPresupuestos: false,
      filtrarProyecto: 0,
    });
  }

  consultarReporte(values) {
    this.setState({ loading: true, ultimosValores: values });
    axios
      .post(
        "reportesFinancieros/reporteFlujoDeEfectivoDiario",
        {
          ...values,
          // Fechas-dia como "YYYY-MM-DD": el objeto moment viaja como instante UTC y en
          // el servidor se correria de dia.
          periodo: formatoPeriodo(values.periodo),
          empresaId: this.props.empresaId,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({ loading: false, dataReporte: res.data });
        } else if (res.data.success === 1) {
          message.warning(res.data.mensaje || "No se pudo generar el reporte");
          this.setState({ loading: false });
        } else {
          message.error("Ha sucedido un error y no se cargo bien el reporte");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Ha sucedido un error y no se cargo bien el reporte");
        this.setState({ loading: false });
      });
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  // Columnas y renglones ya agrupados segun "Por dia / semana / mes". Se memoriza por
  // respuesta y modo: con un ano por dia son cientos de columnas.
  matriz() {
    const { dataReporte, modoColumnas } = this.state;
    if (!dataReporte) return { columnas: [], renglones: [] };
    if (this._matriz && this._matriz.datos === dataReporte && this._matriz.modo === modoColumnas) {
      return this._matriz.resultado;
    }
    const columnas = construirColumnas(dataReporte.dias, modoColumnas);
    const resultado = { columnas, renglones: agruparRenglones(dataReporte.renglones, columnas) };
    this._matriz = { datos: dataReporte, modo: modoColumnas, resultado };
    return resultado;
  }

  descripcionFiltros() {
    const data = this.state.dataReporte;
    if (!data) return "";
    const p = data.parametros || {};
    const partes = [
      `Del ${moment(p.desde, "YYYY-MM-DD").format("DD/MM/YYYY")} al ${moment(p.hasta, "YYYY-MM-DD").format("DD/MM/YYYY")}`,
      `Cuentas: ${(data.bancos || []).map((b) => b.nombre).join(", ") || "ninguna"}`,
      `Proyeccion por fecha de ${p.modoFecha === "vencimiento" ? "vencimiento" : "pago"}`,
    ];
    if (p.incluirPresupuestos) partes.push("con presupuestos");
    if (data.filtrarProyecto === 1 && (data.proyectos || []).length > 0) {
      partes.push(`Proyectos: ${data.proyectos.map((x) => x.nombre).join(", ")}`);
    }
    if (data.filtrarProyecto === 2 && data.unidadDeNegocio && data.unidadDeNegocio.nombre) {
      partes.push(`Unidad de negocio: ${data.unidadDeNegocio.nombre}`);
    }
    return partes.join(" | ");
  }

  exportarExcel() {
    const { dataReporte } = this.state;
    if (!dataReporte) return;
    const { columnas, renglones } = this.matriz();
    descargarExcel({ datos: dataReporte, columnas, renglones, descripcionFiltros: this.descripcionFiltros() });
  }

  abrirDetalle(renglon, columna) {
    const { dataReporte } = this.state;
    const porClave = new Map(dataReporte.renglones.map((r) => [r.clave, r]));
    const movimientos = movimientosDeCelda(dataReporte.movimientos, renglon, columna, porClave).map((m, i) => ({
      ...m,
      llave: i,
    }));
    this.setState({
      modalDetalle: true,
      detalleTitulo: `${renglon.etiqueta} - ${columna.titulo} ${columna.subtitulo}`,
      detalleMovimientos: movimientos,
    });
  }

  cerrarDetalle = () => {
    this.setState({ modalDetalle: false, detalleMovimientos: [] });
  };

  celdaImporte(valor, renglon, columna) {
    if (valor === null || valor === undefined) return "";
    // Los ceros de los renglones de detalle se dejan en blanco para que se lean los
    // movimientos; totales y saldos si muestran su cero.
    if (Number(valor) === 0 && (renglon.tipo === "dato" || renglon.tipo === "informativo")) return "";
    const contenido = dineroDisabledMask(valor);
    const color = Number(valor) < 0 ? ROJO_NEGATIVO : undefined;
    if (!tieneDetalle(renglon)) return <span style={{ color }}>{contenido}</span>;
    return (
      <span
        style={{ color, cursor: "pointer", textDecoration: "underline dotted" }}
        onClick={() => this.abrirDetalle(renglon, columna)}
      >
        {contenido}
      </span>
    );
  }

  cerrarConfiguracion = () => {
    this.setState({ modalConfiguracion: false });
  };

  // Al guardar la configuracion se vuelve a consultar con los mismos filtros, para que
  // las cuentas, las observaciones y la clasificacion nueva se vean de inmediato.
  configuracionGuardada = () => {
    this.setState({ modalConfiguracion: false });
    if (this.state.ultimosValores) this.consultarReporte(this.state.ultimosValores);
  };

  construirColumnasTabla(columnas) {
    const celdaConEstilo = (renglon) => ({ style: estiloDeRenglon(renglon) });
    const resultado = [
      {
        title: "Cuenta",
        key: "cuenta",
        fixed: "left",
        width: 110,
        ellipsis: true,
        onCell: celdaConEstilo,
        render: (_, renglon) => {
          const texto = textoCuentas(renglon);
          if (!texto) return "";
          return (
            <Tooltip title={(renglon.cuentas || []).map((c) => `${c.codigo} ${c.descripcion}`).join(" · ")}>
              <span>{texto}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Observaciones",
        key: "observaciones",
        dataIndex: "observaciones",
        fixed: "left",
        width: 200,
        ellipsis: true,
        onCell: celdaConEstilo,
        render: (valor) =>
          valor ? (
            <Tooltip title={valor}>
              <span style={{ fontSize: 12 }}>{valor}</span>
            </Tooltip>
          ) : (
            ""
          ),
      },
      {
        title: "Concepto",
        key: "concepto",
        dataIndex: "etiqueta",
        fixed: "left",
        width: 280,
        ellipsis: true,
        onCell: celdaConEstilo,
        render: (valor, renglon) => (
          <span style={{ paddingLeft: renglon.tipo === "dato" || renglon.tipo === "informativo" ? 12 : 0 }}>
            {valor}
            {renglon.tipo === "informativo" ? " (informativo)" : ""}
          </span>
        ),
      },
    ];
    columnas.forEach((columna, indice) => {
      resultado.push({
        title: (
          <div style={{ textAlign: "center", lineHeight: 1.2 }}>
            <div>{columna.titulo}</div>
            <div style={{ fontSize: 11, fontWeight: 400 }}>{columna.subtitulo}</div>
            <div style={{ fontSize: 10, fontWeight: 400, opacity: 0.85 }}>{ETIQUETA_TIPO_COLUMNA[columna.tipo]}</div>
          </div>
        ),
        key: columna.key,
        align: "right",
        width: 125,
        onHeaderCell: () => ({ style: COLOR_ENCABEZADO[columna.tipo] }),
        onCell: celdaConEstilo,
        render: (_, renglon) => (renglon.valores ? this.celdaImporte(renglon.valores[indice], renglon, columna) : ""),
      });
    });
    return resultado;
  }

  bloqueTotales() {
    const totales = (this.state.dataReporte && this.state.dataReporte.totales) || {};
    const tarjeta = (titulo, valor, color) => (
      <Col xs={24} sm={12} md={6}>
        <Card size="small" style={{ borderTop: `3px solid ${color}` }}>
          <Statistic
            title={titulo}
            value={dineroDisabledMask(valor || 0)}
            valueStyle={{ fontSize: 18, color: Number(valor) < 0 ? ROJO_NEGATIVO : undefined }}
          />
        </Card>
      </Col>
    );
    return (
      <Row gutter={[10, 10]} style={{ marginTop: 10 }}>
        {tarjeta("Saldo inicial", totales.saldoInicial, "#00BFD6")}
        {tarjeta("Total de ingresos", totales.totalIngresos, "#237804")}
        {tarjeta("Total de egresos", totales.totalEgresos, "#A8071A")}
        {tarjeta("Remanente final", totales.saldoFinal, "#007A8A")}
      </Row>
    );
  }

  modalDetalle() {
    const { detalleMovimientos } = this.state;
    const etiquetas = new Map(((this.state.dataReporte && this.state.dataReporte.renglones) || []).map((r) => [r.clave, r.etiqueta]));
    const total = Math.round(detalleMovimientos.reduce((acc, m) => acc + (Number(m.montoEnCelda) || 0), 0) * 100) / 100;
    const columnas = [
      {
        title: "Fecha",
        dataIndex: "fecha",
        key: "fecha",
        width: 95,
        render: (valor) => moment(valor, "YYYY-MM-DD").format("DD/MM/YYYY"),
      },
      {
        title: "Origen",
        dataIndex: "origen",
        key: "origen",
        width: 105,
        render: (valor) => <Tag color={COLOR_TAG_ORIGEN[valor]}>{ETIQUETA_ORIGEN[valor] || valor}</Tag>,
      },
      { title: "Renglon", dataIndex: "clave", key: "clave", width: 200, render: (valor) => etiquetas.get(valor) || valor },
      { title: "Documento", dataIndex: "documento", key: "documento", width: 120 },
      { title: "Cliente / Proveedor", dataIndex: "tercero", key: "tercero", width: 200, ellipsis: true },
      { title: "Cuenta contable", dataIndex: "cuenta", key: "cuenta", width: 180, ellipsis: true },
      { title: "Poliza", dataIndex: "poliza", key: "poliza", width: 110 },
      { title: "Concepto", dataIndex: "concepto", key: "concepto", width: 220, ellipsis: true },
      {
        title: "Monto",
        dataIndex: "montoEnCelda",
        key: "monto",
        align: "right",
        width: 130,
        render: (valor) => (
          <span style={{ color: Number(valor) < 0 ? ROJO_NEGATIVO : undefined }}>{dineroDisabledMask(valor || 0)}</span>
        ),
      },
    ];
    return (
      <Modal
        visible={this.state.modalDetalle}
        title={this.state.detalleTitulo}
        onCancel={this.cerrarDetalle}
        footer={null}
        width="90%"
        destroyOnClose
      >
        <Table
          className="custom-small-table"
          size="small"
          bordered
          columns={columnas}
          dataSource={detalleMovimientos}
          rowKey="llave"
          pagination={{ pageSize: 50, showSizeChanger: false }}
          scroll={detalleMovimientos.length ? { x: "max-content", y: "55vh" } : undefined}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={8}>
                <b>Total</b>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8} align="right">
                <b>{dineroDisabledMask(total)}</b>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      </Modal>
    );
  }

  render() {
    const { dataReporte, modoColumnas } = this.state;
    const { columnas, renglones } = this.matriz();

    return (
      <>
        <Form
          ref={this.formRef}
          name="reporteFlujoDeEfectivoDiario"
          onFinish={this.consultarReporte.bind(this)}
          onFinishFailed={this.onFinishFailed}
        >
          <PageHeader
            onBack={this.atras.bind(this)}
            title="Reporte Flujo de Efectivo Diario"
            subTitle={dataReporte ? nombreEmpresa(dataReporte.empresa) : undefined}
            style={{
              border: "1px solid rgb(235, 237, 240)",
              backgroundColor: "white",
              marginBottom: 10,
            }}
            extra={
              <div style={{ display: "flex", justifyContent: "right" }}>
                <Space>
                  <ButtonItem
                    blanco
                    onClick={() => this.setState({ modalConfiguracion: true })}
                    icon={<SettingOutlined />}
                  >
                    Configurar cuentas
                  </ButtonItem>
                  {dataReporte && (
                    <ButtonItem blanco onClick={() => this.exportarExcel()} icon={<FileExcelOutlined />}>
                      Descargar Excel
                    </ButtonItem>
                  )}
                  <ButtonItem type="primary" htmlType="submit">
                    Consultar
                  </ButtonItem>
                </Space>
              </div>
            }
          />
          <Spin spinning={this.state.loading}>
            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingTop: 5,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
              }}
            >
              <Tabs
                style={{ margin: 0 }}
                tabBarExtraContent={{
                  left: <h1 style={{ padding: 0, margin: "0 10px 0 0" }}>Filtros:</h1>,
                }}
              >
                <TabPane tabKey="1" key="1" forceRender tab="Periodo">
                  <FormItem
                    initialValue={[moment().startOf("month"), moment().add(30, "days")]}
                    name="periodo"
                    required
                    noStyle
                  >
                    <RangePickerItem />
                  </FormItem>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 6 }}>
                    Antes de hoy se muestran los movimientos reales de los bancos; de hoy en adelante, la
                    proyeccion con las cuentas por cobrar y por pagar pendientes. Maximo 400 dias.
                  </div>
                </TabPane>
                <TabPane tabKey="2" key="2" forceRender tab="Cuentas">
                  <Row>
                    <Col span={16}>
                      <FormItem name="bancos" noStyle initialValue={[]}>
                        <SelectItem width="95%" mode="multiple" allowClear placeholder="Todos los bancos activos">
                          {this.state.objetoBancos}
                        </SelectItem>
                      </FormItem>
                    </Col>
                    <Col span={8}>
                      <FormItem
                        label="Incluir cajas"
                        name="incluirCajas"
                        valuePropName="checked"
                        marginBottom="0px"
                        initialValue={false}
                      >
                        <SwitchItem />
                      </FormItem>
                    </Col>
                  </Row>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 6 }}>
                    Sin seleccion se toman todos los bancos activos (y las cajas si se activa el switch). Los
                    traspasos entre cuentas incluidas no se muestran; un retiro hacia una caja no incluida sale
                    como "Retiro de efectivo".
                  </div>
                </TabPane>
                <TabPane tabKey="3" key="3" forceRender tab="Proyeccion">
                  <Row>
                    <Col span={12}>
                      <FormItem label="Proyectar con" name="modoFecha" required marginBottom="0px" initialValue="pago">
                        <SelectItem placeholder="Fecha para proyectar">
                          <Option value="pago">Fecha de pago (promesa)</Option>
                          <Option value="vencimiento">Fecha de vencimiento</Option>
                        </SelectItem>
                      </FormItem>
                    </Col>
                    <Col span={12}>
                      <FormItem
                        label="Incluir presupuestos"
                        name="incluirPresupuestos"
                        valuePropName="checked"
                        marginBottom="0px"
                        initialValue={false}
                      >
                        <SwitchItem />
                      </FormItem>
                    </Col>
                  </Row>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 6 }}>
                    Lo vencido por cobrar se muestra en "Vencido" y no suma; lo vencido por pagar si suma, en la
                    columna de hoy. Los presupuestos que se incluyen son los marcados "Mostrar en flujo".
                  </div>
                </TabPane>
                <TabPane tabKey="4" key="4" forceRender tab="Proyecto">
                  <Row>
                    <Col span={8}>
                      <FormItem name="filtrarProyecto" required noStyle initialValue={0}>
                        <SelectItem width="90%" placeholder="Filtrar Proyecto">
                          <Option value={0}>Ninguno</Option>
                          <Option value={1}>Proyectos</Option>
                          <Option value={2}>Unidades de negocio</Option>
                        </SelectItem>
                      </FormItem>
                    </Col>
                    <Col span={8}>
                      <FormItem shouldUpdate noStyle>
                        {({ getFieldValue }) => {
                          const filtrarProyecto = getFieldValue("filtrarProyecto");
                          if (filtrarProyecto === 1) {
                            return (
                              <FormItem required name="proyectos" noStyle>
                                <SelectItem width="90%" placeholder="Proyectos" mode="multiple">
                                  {this.state.objetoProyectos}
                                </SelectItem>
                              </FormItem>
                            );
                          }
                          if (filtrarProyecto === 2) {
                            return (
                              <FormItem required name="unidadDeNegocioId" noStyle>
                                <SelectItem width="90%" placeholder="Unidades de Negocio">
                                  {this.state.objetoUnidadesDeNegocio}
                                </SelectItem>
                              </FormItem>
                            );
                          }
                          return null;
                        }}
                      </FormItem>
                    </Col>
                  </Row>
                </TabPane>
              </Tabs>
            </div>

            {dataReporte && this.bloqueTotales()}

            {dataReporte && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: 10,
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <Space wrap>
                  <Tag style={{ ...COLOR_ENCABEZADO.real, border: 0 }}>Real</Tag>
                  <Tag style={{ ...COLOR_ENCABEZADO.hoy, border: 0 }}>Hoy</Tag>
                  <Tag style={{ ...COLOR_ENCABEZADO.proyectado, border: 0 }}>Proyectado</Tag>
                  <Tooltip title="Da clic en un importe para ver los movimientos que lo forman">
                    <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                      <InfoCircleOutlined /> Clic en un importe para ver su detalle
                    </span>
                  </Tooltip>
                </Space>
                <Select
                  value={modoColumnas}
                  onChange={(valor) => this.setState({ modoColumnas: valor })}
                  style={{ width: 160 }}
                >
                  {MODOS_COLUMNA.map((modo) => (
                    <Option value={modo.valor} key={modo.valor}>
                      {modo.etiqueta}
                    </Option>
                  ))}
                </Select>
              </div>
            )}

            <div style={{ marginTop: 10 }}>
              <Table
                className="custom-small-table"
                columns={this.construirColumnasTabla(columnas)}
                dataSource={renglones}
                rowKey={(renglon) => renglon.clave}
                size="small"
                bordered
                pagination={false}
                scroll={renglones.length ? { x: "max-content", y: "65vh" } : undefined}
              />
            </div>
          </Spin>
        </Form>
        {this.modalDetalle()}
        <ModalConfiguracionFlujo
          visible={this.state.modalConfiguracion}
          empresaId={this.props.empresaId}
          headersToken={this.props.headersToken}
          onCancel={this.cerrarConfiguracion}
          onGuardado={this.configuracionGuardada}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(ReporteFlujoDeEfectivoDiario);
